import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Session } from '@ory/client';
import { Observable, combineLatest, take } from 'rxjs';

import { OryFlowType } from '../constants/ory.constant';
import { OryAuthPages } from '../constants/pages.constant';
import * as OryAuthActions from './ory-auth.actions';
import {
  AuthError,
  LoginFlow,
  LoginFormData,
  OtpFormData,
  RegistrationFlow,
  RegistrationFormData,
} from './ory-auth.models';
import {
  selectAnyError,
  selectCanResend,
  selectCurrentCsrfToken,
  selectCurrentFlowId,
  selectCurrentFlowType,
  selectIsAuthenticated,
  selectIsLoading,
  selectIsResending,
  selectIsSubmitting,
  selectLoginCsrfToken,
  selectLoginData,
  selectLoginFlow,
  selectLoginFlowError,
  selectLoginFlowId,
  selectLoginFlowLoading,
  selectLogoutError,
  selectLogoutLoading,
  selectRegistrationCsrfToken,
  selectRegistrationData,
  selectRegistrationFlow,
  selectRegistrationFlowError,
  selectRegistrationFlowId,
  selectRegistrationFlowLoading,
  selectRegistrationPhase,
  selectResendCountdown,
  selectResendError,
  selectSession,
  selectSessionError,
  selectSessionLoading,
  selectSubmitError,
} from './ory-auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class OryAuthFacade {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  readonly session$: Observable<Session | null> =
    this.store.select(selectSession);

  readonly isAuthenticated$: Observable<boolean> = this.store.select(
    selectIsAuthenticated,
  );
  readonly sessionLoading$: Observable<boolean> =
    this.store.select(selectSessionLoading);
  readonly sessionError$: Observable<AuthError | null> =
    this.store.select(selectSessionError);

  // Current Flow State
  readonly currentFlowId$: Observable<string | null> =
    this.store.select(selectCurrentFlowId);
  readonly currentFlowType$: Observable<OryFlowType | string | null> =
    this.store.select(selectCurrentFlowType);
  readonly currentCsrfToken$: Observable<string | null> = this.store.select(
    selectCurrentCsrfToken,
  );

  // Pending Registration Data
  readonly pendingRegistrationData$: Observable<RegistrationFormData | null> =
    this.store.select(selectRegistrationData);

  // Pending Login data
  readonly pendingLoginData$: Observable<LoginFormData | null> =
    this.store.select(selectLoginData);

  readonly loginFlow$: Observable<LoginFlow | null> =
    this.store.select(selectLoginFlow);
  readonly loginFlowId$: Observable<string | undefined> =
    this.store.select(selectLoginFlowId);
  readonly loginFlowLoading$: Observable<boolean> = this.store.select(
    selectLoginFlowLoading,
  );
  readonly loginFlowError$: Observable<AuthError | null> =
    this.store.select(selectLoginFlowError);
  readonly loginCsrfToken$: Observable<string | null> =
    this.store.select(selectLoginCsrfToken);

  readonly registrationFlow$: Observable<RegistrationFlow | null> =
    this.store.select(selectRegistrationFlow);
  readonly registrationFlowId$: Observable<string | undefined> =
    this.store.select(selectRegistrationFlowId);
  readonly registrationFlowLoading$: Observable<boolean> = this.store.select(
    selectRegistrationFlowLoading,
  );
  readonly registrationFlowError$: Observable<AuthError | null> =
    this.store.select(selectRegistrationFlowError);
  readonly registrationCsrfToken$: Observable<string | null> =
    this.store.select(selectRegistrationCsrfToken);
  readonly registrationPhase$: Observable<'email' | 'code'> = this.store.select(
    selectRegistrationPhase,
  );

  readonly isSubmitting$: Observable<boolean> =
    this.store.select(selectIsSubmitting);

  readonly submitError$: Observable<AuthError | null> =
    this.store.select(selectSubmitError);

  readonly isResending$: Observable<boolean> =
    this.store.select(selectIsResending);

  readonly resendError$: Observable<AuthError | null> =
    this.store.select(selectResendError);

  readonly resendCountdown$: Observable<number> = this.store.select(
    selectResendCountdown,
  );

  readonly canResend$: Observable<boolean> = this.store.select(selectCanResend);

  readonly isLoading$: Observable<boolean> = this.store.select(selectIsLoading);

  readonly logoutLoading$: Observable<boolean> =
    this.store.select(selectLogoutLoading);

  readonly logoutError$: Observable<AuthError | null> =
    this.store.select(selectLogoutError);

  readonly anyError$: Observable<AuthError | null> =
    this.store.select(selectAnyError);

  redirectToLogin(): void {
    this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.LOGIN]);
  }

  redirectToRegistration(): void {
    this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.REGISTER]);
  }

  redirectToOtpVerify(flowId: string, email?: string): void {
    this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.OTP], {
      queryParams: { flow: flowId, email },
    });
  }

  loadSession(): void {
    this.store.dispatch(OryAuthActions.loadSession());
  }

  clearSession(): void {
    this.store.dispatch(OryAuthActions.clearSession());
  }

  startLoginFlow(redirectTo?: string): void {
    this.store.dispatch(OryAuthActions.startLoginFlow({ redirectTo }));
  }

  loadLoginFlow(flowId: string): void {
    this.store.dispatch(OryAuthActions.loadLoginFlow({ flowId }));
  }

  submitLogin(formData: LoginFormData): void {
    this.store
      .select((state) => ({
        flowId: selectCurrentFlowId(state),
        csrfToken: selectCurrentCsrfToken(state),
      }))
      .pipe(take(1))
      .subscribe(({ flowId, csrfToken }) => {
        if (flowId && csrfToken) {
          this.store.dispatch(
            OryAuthActions.submitLogin({
              flowId,
              formData: { ...formData, csrf_token: csrfToken },
            }),
          );
        }
      });
  }

  setRedirectParams(redirectTo: string): void {
    this.store.dispatch(OryAuthActions.setRedirectParams({ redirectTo }));
  }

  startRegistrationFlow(redirectTo?: string): void {
    this.store.dispatch(OryAuthActions.startRegistrationFlow({ redirectTo }));
  }

  loadRegistrationFlow(flowId: string): void {
    this.store.dispatch(OryAuthActions.loadRegistrationFlow({ flowId }));
  }

  submitRegistration(formData: RegistrationFormData): void {
    combineLatest([this.currentFlowId$, this.currentCsrfToken$])
      .pipe(take(1))
      .subscribe(([flowId, csrfToken]) => {
        if (flowId && csrfToken) {
          this.store.dispatch(
            OryAuthActions.submitRegistration({
              flowId,
              formData: { ...formData, csrf_token: csrfToken },
            }),
          );
        }
      });
  }

  submitOtp(formData: OtpFormData): void {
    this.store
      .select((state) => ({
        flowId: selectCurrentFlowId(state),
        flowType: selectCurrentFlowType(state),
        csrfToken: selectCurrentCsrfToken(state),
        registrationData: selectRegistrationData(state),
        loginData: selectLoginData(state),
      }))
      .pipe(take(1))
      .subscribe(
        ({ flowId, flowType, csrfToken, registrationData, loginData }) => {
          if (flowId && flowType && csrfToken) {
            this.store.dispatch(
              OryAuthActions.submitOtp({
                flowId,
                flowType,
                csrfToken,
                otpFormData: formData,
                registrationData:
                  flowType === OryFlowType.REGISTRATION
                    ? registrationData || undefined
                    : undefined,
                loginData:
                  flowType === OryFlowType.LOGIN
                    ? loginData || undefined
                    : undefined,
              }),
            );
          }
        },
      );
  }

  resendOtp(): void {
    this.store
      .select((state) => ({
        flowId: selectCurrentFlowId(state),
        flowType: selectCurrentFlowType(state),
        csrfToken: selectCurrentCsrfToken(state),
        registrationData: selectRegistrationData(state),
        loginData: selectLoginData(state),
      }))
      .pipe(take(1))
      .subscribe(
        ({ flowId, flowType, csrfToken, registrationData, loginData }) => {
          if (flowId && flowType && csrfToken) {
            this.store.dispatch(
              OryAuthActions.resendOtp({
                flowId,
                flowType,
                csrfToken,
                registrationData:
                  flowType === OryFlowType.REGISTRATION
                    ? registrationData || undefined
                    : undefined,
                loginData:
                  flowType === OryFlowType.LOGIN
                    ? loginData || undefined
                    : undefined,
              }),
            );
          }
        },
      );
  }

  logout(): void {
    this.store.dispatch(OryAuthActions.logout());
  }

  navigateToLogin(flowId?: string): void {
    this.store.dispatch(OryAuthActions.navigateToLogin({ flowId }));
  }

  navigateToRegistration(flowId?: string): void {
    this.store.dispatch(OryAuthActions.navigateToRegistration({ flowId }));
  }

  navigateToOtpVerify(flowId: string, email?: string): void {
    this.store.dispatch(OryAuthActions.navigateToOtpVerify({ flowId, email }));
  }

  navigateToLanding(): void {
    this.store.dispatch(OryAuthActions.navigateToLanding());
  }

  clearErrors(): void {
    this.store.dispatch(OryAuthActions.clearErrors());
  }

  clearFlowState(): void {
    this.store.dispatch(OryAuthActions.clearFlowState());
  }

  removeResendCountdown(): void {
    this.store.dispatch(OryAuthActions.removeResendCountdown());
  }

  handleFlowExpired(flowType: OryFlowType): void {
    this.store.dispatch(OryAuthActions.handleFlowExpired({ flowType }));
  }
}
